import { useState } from 'react'
import { useNavigate } from 'react-router'
import { formatNumber, percent } from '@/lib/format'
import { cn } from '@/lib/utils'
import type { HorizonTeam } from '@/lib/types'

const HEIGHT = 168 // drawing area, px
const TOP_PAD = 14
const MAX_USAGE = 1.25 // a building over its limit still fits

const hash = (n: number) => ((n * 2654435761) >>> 0) / 4294967296

/**
 * Every team as a building, left to right from the fullest. Height is the share
 * of the plan's company limit in use, so tall means close to the limit; the
 * dashed line is 100%. A cap marks a team near (amber) or over (red) its limit,
 * and lit windows mean someone signed in or data arrived in the last week.
 */
export function Horizon({ teams }: { teams: HorizonTeam[] }) {
  const navigate = useNavigate()
  const [focus, setFocus] = useState<HorizonTeam | null>(null)

  if (teams.length === 0) return null
  const bw = teams.length <= 12 ? 40 : teams.length <= 30 ? 26 : 16
  const gap = bw > 20 ? 8 : 4
  const width = teams.length * (bw + gap) + gap
  const ground = HEIGHT + TOP_PAD
  const limitY = ground - (1 / MAX_USAGE) * HEIGHT

  return (
    <figure>
      <div className="overflow-x-auto pb-1">
        <svg
          width={width}
          height={ground + 6}
          viewBox={`0 0 ${width} ${ground + 6}`}
          className="block min-w-full"
          role="group"
          aria-label="Teams as buildings, by how much of their company limit they use"
        >
          <line x1="0" x2={width} y1={limitY} y2={limitY} stroke="var(--muted-foreground)" strokeOpacity="0.45" strokeDasharray="3 4" />
          <text x={width - 4} y={limitY - 5} textAnchor="end" fontSize="10" fill="var(--muted-foreground)">
            plan limit
          </text>

          {teams.map((t, i) => {
            const usage = Math.min(t.usage, MAX_USAGE)
            const h = Math.max(12, (usage / MAX_USAGE) * HEIGHT)
            const x = gap + i * (bw + gap)
            const y = ground - h
            const paid = t.plan_id !== 'free'
            const over = t.flags.includes('over_company_limit')
            const near = t.flags.includes('near_company_limit')
            const cols = bw > 20 ? 3 : bw > 14 ? 2 : 1
            const pitch = (bw - 4) / cols
            const rows = Math.max(0, Math.floor((h - 10) / 8))
            const isFocus = focus?.id === t.id

            return (
              <g
                key={t.id}
                role="link"
                tabIndex={0}
                aria-label={`${t.name}: ${formatNumber(t.companies)} of ${formatNumber(t.limit)} companies`}
                className="cursor-pointer outline-none"
                onClick={() => navigate(`/teams/${t.id}`)}
                onKeyDown={(e) => e.key === 'Enter' && navigate(`/teams/${t.id}`)}
                onMouseEnter={() => setFocus(t)}
                onMouseLeave={() => setFocus((f) => (f?.id === t.id ? null : f))}
                onFocus={() => setFocus(t)}
                onBlur={() => setFocus((f) => (f?.id === t.id ? null : f))}
              >
                <rect x={x - gap / 2} y={0} width={bw + gap} height={ground} fill="transparent" />
                <rect
                  x={x}
                  y={y}
                  width={bw}
                  height={h}
                  rx="2"
                  className={cn(paid ? 'fill-building-paid' : 'fill-building')}
                  opacity={isFocus ? 1 : 0.88}
                />
                {(over || near) && <rect x={x} y={y} width={bw} height="3" rx="1.5" fill={over ? 'var(--risk)' : 'var(--warn)'} />}
                {Array.from({ length: rows }, (_, r) =>
                  Array.from({ length: cols }, (_, c) => {
                    const lit = t.active && hash(t.id * 131 + r * 17 + c) < 0.62
                    return (
                      <rect
                        key={`${r}-${c}`}
                        x={x + 3 + c * pitch + (pitch - 3) / 2}
                        y={y + 8 + r * 8}
                        width="3"
                        height="4"
                        rx="0.6"
                        fill={lit ? 'var(--window)' : 'var(--foreground)'}
                        opacity={lit ? 1 : 0.1}
                        className={lit ? 'skyline-window' : undefined}
                        style={lit ? { animationDelay: `${Math.min(i * 25 + r * 40, 1100)}ms` } : undefined}
                      />
                    )
                  }),
                )}
                {isFocus && <rect x={x - 1} y={y - 1} width={bw + 2} height={h + 2} rx="3" fill="none" stroke="var(--ring)" strokeWidth="1.5" />}
              </g>
            )
          })}
          <line x1="0" x2={width} y1={ground + 0.5} y2={ground + 0.5} stroke="var(--border)" />
        </svg>
      </div>
      <figcaption className="mt-2 flex min-h-5 flex-wrap items-center gap-x-4 gap-y-1 text-muted-foreground" aria-live="polite">
        {focus ? (
          <span>
            <span className="font-medium text-foreground">{focus.name}</span> · {formatNumber(focus.companies)} of {formatNumber(focus.limit)} companies ({percent(focus.usage)}){' '}
            · {focus.plan_id === 'free' ? 'Free' : 'Paid'} · {focus.active ? 'active this week' : 'quiet this week'}
          </span>
        ) : (
          <>
            <Legend swatch="bg-building-paid" label="Paid plan" />
            <Legend swatch="bg-building" label="Free plan" />
            <Legend swatch="bg-window" label="Active this week" />
            <Legend swatch="bg-warn" label="Near limit" />
            <Legend swatch="bg-risk" label="Over limit" />
          </>
        )}
      </figcaption>
    </figure>
  )
}

function Legend({ swatch, label }: { swatch: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs">
      <span className={cn('size-2.5 rounded-[3px]', swatch)} aria-hidden />
      {label}
    </span>
  )
}
